import { motion } from 'framer-motion';
import { SectionShell } from './SectionShell';

/**
 * Go to market: development panel, warm network, focused sales, then scale.
 *
 * Per the user's instruction, no third-party firm logos on this public
 * surface. The motion stages are presented as the GTM mechanic, with
 * R3 / IPA / Microsoft surfaced as named partnership levers (industry
 * bodies + ecosystem, where naming is legitimate).
 */

const INITIAL_MOTION = [
  {
    num: '01',
    title: 'Panel of firms during development',
    body: 'A standing panel of firms with monthly update calls while we build. Feedback shapes every release, and momentum compounds',
  },
  {
    num: '02',
    title: 'Sell through warm introductions',
    body: 'Convert the founders’ practitioner and industry networks across small, mid market and enterprise firms',
  },
  {
    num: '03',
    title: 'Scale through dedicated sales lanes',
    body: 'Two outbound sales hires target smaller and mid market firms, while the founders and a senior enterprise salesperson pursue larger mid tier and enterprise accounts',
  },
];

const PARTNERSHIPS = [
  {
    label: 'R3',
    body: 'Credibility and distribution into the practitioner community',
  },
  {
    label: 'IPA',
    body: 'Compliance posture and industry engagement',
  },
  {
    label: 'Microsoft 365',
    body: 'M365 integration creates a familiar workflow path for UK practitioner firms',
  },
];

export function GoToMarket() {
  return (
    <SectionShell
      id="gtm"
      index={9}
      density="tight"
      eyebrow="Go to market"
      title={
        <>
          Panel, warm network,{' '}
          <span className="text-brand-bright">then scale</span>
        </>
      }
      intro="Development starts with a panel of firms. Commercial traction moves through warm introductions, then focused sales lanes cover the rest of the market. Industry partnerships amplify distribution"
    >
      <div className="pitch-strip grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border-subtle lg:grid-cols-3">
        {INITIAL_MOTION.map((step, i) => (
          <MotionCell key={step.num} step={step} index={i} />
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-ink-dim">
          Partnership levers
        </span>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {PARTNERSHIPS.map((p, i) => (
            <PartnerCard key={p.label} partner={p} index={i} />
          ))}
        </div>
      </div>
    </SectionShell>
  );
}

function MotionCell({
  step,
  index,
}: {
  step: (typeof INITIAL_MOTION)[number];
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.05 + index * 0.07,
      }}
      className="pitch-cell pitch-cell-interactive flex flex-col gap-3 p-7 sm:p-8"
    >
      <span className="text-[11px] font-medium uppercase tabular-nums tracking-[0.22em] text-brand-bright">
        {step.num}
      </span>
      <h3 className="text-balance text-[18px] font-semibold leading-snug text-ink-strong">
        {step.title}
      </h3>
      <p className="text-[14px] leading-relaxed text-ink-muted">{step.body}</p>
    </motion.div>
  );
}

function PartnerCard({
  partner,
  index,
}: {
  partner: (typeof PARTNERSHIPS)[number];
  index: number;
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
        delay: index * 0.06,
      }}
      className="pitch-card pitch-card-interactive flex flex-col gap-2 rounded-2xl p-6"
    >
      <span className="text-[15px] font-semibold uppercase tracking-[0.18em] text-brand-bright">
        {partner.label}
      </span>
      <p className="text-[13px] leading-relaxed text-ink-muted">
        {partner.body}
      </p>
    </motion.article>
  );
}
